import type { PillarGrant, UserDto, UserRole } from 'chery-contracts';
import { PILLAR_META, PILLARS } from 'chery-kpi-engine';
import type { Pillar } from 'chery-contracts';

export type MatrixLevel = 'NONE' | 'VIEW' | 'EDIT';

export type AccessMatrix = Record<Pillar, MatrixLevel>;

export const MATRIX_LEVELS: readonly MatrixLevel[] = ['NONE', 'VIEW', 'EDIT'];

/** Pillar grants are stored for OWNER only; the other roles' access follows from the role (plan §4). */
export const roleHasPillarGrants = (role: UserRole): boolean => role === 'OWNER';

export const emptyMatrix = (): AccessMatrix => Object.fromEntries(PILLARS.map((pillar) => [pillar, 'NONE'])) as AccessMatrix;

export const matrixFromGrants = (grants: readonly PillarGrant[]): AccessMatrix => {
  const matrix = emptyMatrix();

  for (const grant of grants) {
    matrix[grant.pillar] = grant.access;
  }

  return matrix;
};

/** The grants to send: one per pillar with VIEW or EDIT, in pillar order. */
export const grantsFromMatrix = (matrix: AccessMatrix): PillarGrant[] =>
  PILLARS.flatMap((pillar) => {
    const level = matrix[pillar];

    return level === 'NONE' ? [] : [{ pillar, access: level }];
  });

/** One line for the list: what the user can reach, by role or by grant. */
export const accessSummary = (user: Pick<UserDto, 'role' | 'pillarAccess'>): string => {
  switch (user.role) {
    case 'ADMIN':
    case 'PM':
      return 'All pillars · edit';
    case 'MANAGER':
      return 'All pillars · view';
    case 'OWNER':
      return user.pillarAccess.length === 0
        ? 'No pillars'
        : user.pillarAccess.map((grant) => `${PILLAR_META[grant.pillar].shortLabel} · ${grant.access === 'EDIT' ? 'edit' : 'view'}`).join(', ');
  }
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** `11 Oct 2026, 09:05` in Asia/Amman, or `Never`. */
export const formatSignIn = (iso: string | null): string => {
  if (!iso) {
    return 'Never';
  }

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Amman',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(iso))
      .map(({ type, value }) => [type, value]),
  );

  return `${Number(parts.day)} ${MONTHS[Number(parts.month) - 1]} ${parts.year}, ${parts.hour}:${parts.minute}`;
};

/** True while the per-user login lock is running. */
export const isLocked = (lockedUntil: string | null, now: Date): boolean => lockedUntil !== null && new Date(lockedUntil).getTime() > now.getTime();
